import { useContext, useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { APIContext } from 'context/APIContext'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { TrashIcon } from '@heroicons/react/24/outline'
import kinesisLogo from 'media/kinesis_logo.png'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ContentFrame from 'components/Frame/ContentFrame'
import SearchBar from 'components/Search/SearchBar'
import PaginationController from 'components/Pagination/PaginationController'

const defaultPagination = {
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
}

export default function BoilerplateProgramDirectoryView() {

    const navigate = useNavigate()

    const { GetProgramDirectory, DeleteProgram } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const [programs, setPrograms] = useState(null)
    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [refreshKey, setRefreshKey] = useState(0)
    const [pagination, setPagination] = useState(defaultPagination)

    const appliedSearchRef = useRef('')

    const LoadProgramDirectory = async (abortController) => {
        setPrograms(null)

        const result = await GetProgramDirectory('config', pagination.page, pagination.pageSize, searchTerm, 'all', moment().local().format('YYYY-MM-DD'), { signal: abortController.signal })

        if (abortController.signal.aborted) {
            return
        }

        if (!result) {
            setPrograms([])
            return
        }

        const nextPagination = result.pagination

        if (nextPagination && nextPagination.totalPages > 0 && nextPagination.page > nextPagination.totalPages) {
            setPagination({ ...nextPagination, page: nextPagination.totalPages })
            return
        }

        setPrograms(result.programs ?? [])

        if (nextPagination) {
            setPagination(nextPagination)
        }
    }

    const OnCreateProgramButtonClicked = () => {
        const modalData = {
            key: ModalKey.CreateProgram,
            size: 'third',
        }

        ShowModal(modalData)
    }

    const OnProgramClicked = (program) => {
        navigate(`/program/${program?.programId}/playground`)
    }

    const ShowDeleteProgramModal = (program) => {
        const modalData = {
            key: ModalKey.DeleteProgram,
            size: 'third',
            programId: program?.programId,
            titleText: 'Delete Program "' + program?.name + '"?',
            bodyText: [
                'All data associated with this program will be deleted.',
                'This action cannot be undone.'
            ],
            buttonText: 'Delete',
            buttonAction: async () => {
                const result = await DeleteProgram(program?.programId)

                if (result) {
                    setRefreshKey(key => key + 1)
                }
            },
        }

        ShowModal(modalData)
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const trimmed = searchInput.trim()

            if (appliedSearchRef.current === trimmed) {
                return
            }

            appliedSearchRef.current = trimmed
            setSearchTerm(trimmed)
            setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 })
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchInput])

    useEffect(() => {
        const abortController = new AbortController()
        LoadProgramDirectory(abortController)
        return () => abortController.abort()
    }, [searchTerm, pagination.page, pagination.pageSize, refreshKey])

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Boilerplate Programs
                </h1>

                <div className='w-32'>
                    <BrandColorButton
                        size="extra-small"
                        text="Create"
                        action={OnCreateProgramButtonClicked}
                        className='shadow-md'
                    />
                </div> 
            </div>

            <div className='flex items-center gap-x-4'>
                <div className='flex-grow'>
                    <SearchBar
                        placeholder="Search name or description"
                        searchTerm={searchInput}
                        setSearchTerm={setSearchInput}
                        showFiltersToggle={false}
                    />
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            {programs === null &&
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            }

            {programs !== null &&
                <ContentFrame className='divide-y divide-gray-200'>
                    {programs.length === 0 &&
                        <div className="text-center text-gray-500 text-sm py-3">No programs found</div>
                    }

                    {programs.length > 0 && programs.map((program, index) => (
                        <li
                            key={index}
                            className='w-full flex items-center p-2 gap-x-2 cursor-pointer hover:bg-gray-50 relative'
                            onClick={() => OnProgramClicked(program)}
                        >
                            <div className='pl-1 w-8 flex items-center justify-center'>
                                <img src={kinesisLogo} alt="Kinesis Logo" className="mt-0.5 h-5 w-5" />
                            </div>

                            <p className="text-sm truncate">
                                {program.name}
                            </p>

                            <p className="flex-1 text-xs text-gray-500 truncate">
                                {program.description.length > 0 && <>{program.description}</>}
                                {program.description.length === 0 && <>No description</>}
                            </p>

                            <p className='text-right text-xs text-gray-500'>
                                Created {moment(program.createdTimestamp).format('MMMM DD, YYYY')}
                            </p>

                            <button 
                                className='w-8 h-8 -my-2 flex items-center justify-center'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    ShowDeleteProgramModal(program)
                                }}
                            >
                                <TrashIcon className='w-4 h-4 text-gray-500' />
                            </button>
                        </li>
                    ))}
                </ContentFrame>
            }

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
